import {IItemContent} from "@connox/typescript-library";
import React, {Fragment, useEffect} from "react";
import RatingStars from "../../lib/ratingStars/RatingStars";
import AddToWishlistButton from "../../wishlist/AddToWishlistButton/AddToWishlistButton";
import {useTranslation} from "react-i18next";
import EnergyLabel from "../../energyLabel/EnergyLabel";
import {BrowserStorage} from "../../../storage/BrowserStorage";
import Image from "../../ProductSeries/Image";
import {Badge, BadgeType} from "../../atoms/Badge";

/* Formerly known as 'ItemCard' component */

type IItemCardProps = {
  item: IItemContent;
  onClick?: (evt: React.MouseEvent<HTMLAnchorElement>) => void;
  swipeable?: boolean;
  scrollInfo?: ScrollInfo | null;
  customClass?: string;
  lazyLoading?: boolean;
};

export type ScrollInfo = {
  itemId: number | null;
  position: number;
};

export const scrollInfoStorage = new BrowserStorage<ScrollInfo>({type: "sessionStorage", dataIdentifier: "ScrollInfo"});

const ListItemCard: React.FC<IItemCardProps> = ({
  item,
  onClick,
  swipeable = false,
  scrollInfo,
  customClass = "",
  lazyLoading = false,
}) => {
  const {attributes, daysSincePublishing, images, itemRank, prices, ratingAverage, title, url, itemId} = item;
  const energyEfficiency = attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = images.find((image) => !!image.energyLabel)?.energyLabel || undefined;

  // try to find a price with the quantity of 1, else take the only one in the array
  const {advertise, discountPercentage, price, quantity, retail} =
    prices.find((prc) => prc.quantity.from === 1) || prices[0];
  const {t} = useTranslation();

  // this effect will restore the last scroll position in case scrollinfo prop is passed.
  useEffect(() => {
    if (scrollInfo) {
      const {itemId: storedItemId, position} = scrollInfo;
      if (storedItemId === itemId) {
        if (typeof window.scrollTo === "function") {
          window.scrollTo({left: 0, top: position});
        } else {
          window.scroll(0, position);
        }
        scrollInfoStorage.remove(document.body.id);
      }
    }
  }, []);

  const ItemBadge: React.FC = () => {
    const customBadge = attributes.find((attrib) => attrib.itemAttributeId === 100369);
    const isSetOffer = prices.some((setOfferPrice) => setOfferPrice.quantity.from > 1);

    if (customBadge?.value) {
      return <Badge type={BadgeType.custom} text={customBadge.value} />;
    } else if (isSetOffer) {
      return <Badge type={BadgeType.set} text={t("product.setOffer")} />;
    } else if (advertise && discountPercentage && discountPercentage > 5) {
      return <Badge type={BadgeType.sale} text={`-${discountPercentage}`} />;
    } else if (itemRank && itemRank < 300) {
      return <Badge type={BadgeType.top} text="TOP" />;
    } else if (daysSincePublishing && daysSincePublishing < 30) {
      const newText = document.body.dataset.lang === "de" ? "NEU" : "NEW";

      return <Badge type={BadgeType.new} text={newText} />;
    } else {
      return null;
    }
  };

  const [manufacturer, itemName] = title.split(/\s[-\u{2013}]\s/u, 2);

  return (
    <a
      className={["item-card", customClass, swipeable ? "item-card--swipeable" : ""].join(" ")}
      href={url}
      title={t("general.open--title", {title})}
      onClick={onClick}
    >
      <div className="item-card__placeholder">
        <Image item={item} lazyLoading={lazyLoading} />
      </div>
      <ItemBadge />
      <div className="item-card__title">
        <div className="item-card__brand-name">{manufacturer}</div>
        <div className="item-card__brand-item">{itemName}</div>
      </div>
      <div className="item-card__details">
        <div className="item-card__price__wrapper">
          {!!energyEfficiency && (
            <EnergyLabel energyClass={energyEfficiency.value ?? "G"} size={"small"} media={energyLabel} />
          )}
          <span className="item-card__price">
            {quantity.from > 1 && <span>{t("general.up")}&nbsp;</span>}
            {advertise && (
              <Fragment>
                <s>{t("general.rrp--price", {price: retail.formatted})}</s>{" "}
              </Fragment>
            )}
            <div className={advertise ? "red" : undefined}>
              <span>{price.formatted}</span>
              <sup className="footnote-reference">*</sup>
            </div>
          </span>
        </div>
        {!!ratingAverage.count && (
          <div className="item-card__rating">
            <RatingStars metaProp={false} rating={ratingAverage.average / 10} size={"small"} />
            <span className="item-card__rating__count">({ratingAverage.count})</span>
          </div>
        )}
      </div>
      <AddToWishlistButton itemId={itemId} animated={true} size={"small"} customClass={"item-card__wl-icon"} />
    </a>
  );
};

export default ListItemCard;
